import type { Condition, RecordingStatus } from './api'

/**
 * What each terminal recording status means to a user, in one place.
 *
 * ── THIS IS NOT THE CLIENT DECIDING ─────────────────────────────────────────
 *
 * Acceptance criterion 2 forbids the client inventing a *scientific* claim — a
 * score, a threshold, a result label. A recording's `status` is none of those:
 * it is a state-machine value the backend wrote, and turning `empty` into the
 * words "No signal recorded" is the same kind of act as turning `pending_approval`
 * into "Pending approval", which `StatusPill` has done since 1.8.
 *
 * ── THE ONE THAT MATTERS IS `empty` ─────────────────────────────────────────
 *
 * Sprint 1.2 §9 split `empty` out of `rejected` **precisely so this screen does
 * not have to infer it from a null field**. An `empty` recording carried no
 * scoreable signal, was never scored, and therefore has no Daily Status and no QC
 * reason — `GET /recordings/{id}/daily-status` answers 404 for it. §2.6 says it
 * must be labelled "no signal recorded", **never "failed"**, and that no reason be
 * hunted for. `isError: false` is that rule made mechanical: a screen that styles
 * by `isError` cannot accidentally render it as a failure.
 *
 * ── THE FOUR MISSING KEYS ARE DELIBERATE ────────────────────────────────────
 *
 * `pending_upload`, `uploaded`, `queued` and `processing` are reserved
 * compatibility values with **no writer** (§2.5). They are absent from this map,
 * so a lookup misses and the caller renders "unknown state" — which is the
 * honest thing to say about a value that means the backend changed. The UI must
 * never *wait* on them: while a recording is being made, the authority on
 * progress is `Job.status`, not this.
 */

/** The eight wire values, or anything else the server might one day send. */
export type RecordingStatusLike = RecordingStatus | (string & {})

export interface RecordingOutcome {
  /** The words on the pill. */
  label: string
  /** The sentence beside it, where there is one to say. */
  detail: string | null
  /**
   * Whether this outcome is a failure. `rejected` and `empty` are **not**:
   * one is a quality decision with a reason attached, the other is a recording
   * that simply held nothing.
   */
  isError: boolean
  /**
   * Whether a persisted Daily Status exists to fetch.
   *
   * `hasDailyStatus` in `dailyStatus.ts` READS THIS rather than restating it.
   * Three copies of the same four-value fact were written during Stage 3b and
   * none of them was load-bearing, which is how they drift: the day `empty`
   * gains a result, or `rejected` loses one, only one copy gets edited and
   * nothing goes red.
   */
  hasResult: boolean
}

export const RECORDING_OUTCOME: Readonly<Record<string, RecordingOutcome | undefined>> = {
  scored: {
    label: 'Scored',
    detail: null,
    isError: false,
    hasResult: true,
  },
  rejected: {
    label: 'Rejected',
    detail:
      'This recording did not pass the quality checks, so it was not used. It is kept, ' +
      'and the reason is shown with it — suppressed is not deleted.',
    isError: false,
    hasResult: true,
  },
  empty: {
    // Never "failed", never "error". §2.6, and acceptance criterion 8.
    label: 'No signal recorded',
    detail:
      'This recording carried no scoreable signal, so it was never scored. There is no ' +
      'result and no quality reason to show — nothing went wrong with the processing.',
    isError: false,
    hasResult: false,
  },
  failed: {
    label: 'Failed',
    detail:
      'Processing did not finish. The detail comes from the job that ran it, not from ' +
      'the recording.',
    isError: true,
    hasResult: false,
  },
}

/**
 * Newest recording DATE last, so a `reduce` over it picks the latest.
 *
 * `session_date` and not `created_at`: §1.1 asks for "the actual recording date",
 * and the list this sorts came back ordered by insert time. `created_at` breaks a
 * tie so that two windows derived from one session on the same date have a stable
 * order rather than an arbitrary one.
 */
export function bySessionDate(
  a: { session_date: string; created_at: string },
  b: { session_date: string; created_at: string },
): number {
  if (a.session_date !== b.session_date) return a.session_date < b.session_date ? -1 : 1
  if (a.created_at !== b.created_at) return a.created_at < b.created_at ? -1 : 1
  return 0
}

/* ── Condition ───────────────────────────────────────────────────────────── */

/**
 * The two recording conditions, with the words that make the codes readable.
 *
 * `EC` and `EO` are the wire values and are what a user sees stamped on their
 * own recordings, so both the code and its meaning are shown rather than one
 * being translated away. There is deliberately no third option and no "either":
 * §2.2 requires the condition to be explicit on every upload, because mixing
 * conditions in one baseline is a correctness bug the UI can prevent.
 */
export const CONDITION_OPTIONS: readonly {
  value: Condition
  label: string
  description: string
}[] = [
  { value: 'EC', label: 'EC', description: 'eyes closed' },
  { value: 'EO', label: 'EO', description: 'eyes open' },
] as const

/** The long form, for prose and for a screen's lead line. */
export const CONDITION_WORDS: Record<Condition, string> = {
  EC: 'Eyes closed',
  EO: 'Eyes open',
}
